import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Building2,
  Users,
  SprayCan,
  Globe,
  DollarSign,
  BarChart3,
  Briefcase,
  Settings,
  Wrench,
  BedDouble,
  ShieldCheck,
  Moon,
  Building,
  UsersRound,
  UserCircle,
  TrendingUp,
  DoorOpen,
  Receipt,
  Home,
  ShoppingBag,
  Grid3X3,
  PackageSearch,
  Coins,
  Tag,
  Ban,
  Percent,
  Package,
  Mail,
  Banknote,
  Wallet,
  Truck,
  SlidersHorizontal,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import { UserRole } from '@pms/shared';

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  /** If true, the user can see the item but cannot navigate to it */
  readOnly?: boolean;
  /** Short blurb shown on the property-hub tile launcher */
  description?: string;
}

export interface NavSection {
  /** Section header label. Empty string means no header (standalone items). */
  title: string;
  items: NavItem[];
}

// Top-level groups mirror the HotelKey property hub: Operations / Revenue
// Management / RetailKey / Administration. The same sections drive both the
// sidebar and the tile-launcher Home hub (PropertyHubPage).

// ─── Admin navigation ─────────────────────────────────────────────────────────

const ADMIN_SECTIONS: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Home', path: '/hub', icon: Home },
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    title: 'OPERATIONS',
    items: [
      { label: 'Front Desk', path: '/front-desk', icon: DoorOpen, description: 'Arrivals, departures, in-house and availability' },
      { label: 'Reservations', path: '/reservations', icon: Calendar, description: 'Search and manage reservations' },
      { label: 'Group Bookings', path: '/group-bookings', icon: Users, description: 'Group master and block management' },
      { label: 'Guests', path: '/guests', icon: UserCircle, description: 'Guest profiles and history' },
      { label: 'Bills', path: '/bills', icon: Receipt, description: 'Folios, invoices and payments' },
      { label: 'Housekeeping', path: '/housekeeping', icon: SprayCan, description: 'Room status and service tasks' },
      { label: 'Maintenance', path: '/maintenance', icon: Wrench, description: 'Issues, out-of-order and engineering' },
      { label: 'Shift Management', path: '/shift-management', icon: DollarSign, description: 'Payment summary for a shift' },
      { label: 'Misc Expense', path: '/misc-expense', icon: Receipt, description: 'Log miscellaneous expenses' },
      { label: 'Misc Revenue', path: '/misc-revenue', icon: TrendingUp, description: 'Log miscellaneous revenue' },
      { label: 'House Accounts', path: '/house-accounts', icon: Building, description: 'Non-guest ledgers and balances' },
      { label: 'House Inventory', path: '/house-inventory', icon: Grid3X3, description: 'Inventory grid by room type and date' },
      { label: 'Lost & Found', path: '/lost-found', icon: PackageSearch, description: 'Track lost-and-found items' },
      { label: 'Night Audit', path: '/night-audit', icon: Moon, description: 'Roll the business day and post charges' },
      { label: 'Companies & Direct Bill', path: '/companies', icon: Building, description: 'Company accounts, AR and invoices' },
      { label: 'Reports', path: '/reports', icon: BarChart3, description: 'Dynamic reports and statistics' },
      { label: 'Report Scheduler', path: '/reports/scheduler', icon: BarChart3, description: 'Queue and schedule report runs' },
      { label: 'Data Extracts', path: '/reports/extracts', icon: BarChart3, description: 'Custom data extracts and CSV exports' },
      { label: 'Statistics', path: '/reports/statistics', icon: TrendingUp, description: 'Revenue and room-sold statistics' },
    ],
  },
  {
    title: 'REVENUE MANAGEMENT',
    items: [
      { label: 'Rate Calendar', path: '/rate-calendar', icon: CalendarDays, description: 'Rates and restrictions by date' },
      { label: 'AxisRooms Sync', path: '/rate-parity', icon: RefreshCw, description: 'Rate & availability parity with AxisRooms' },
      { label: 'Rate Administration', path: '/pricing', icon: TrendingUp, description: 'Pricing strategy and overrides' },
      { label: 'Rate Plans', path: '/rate-plans', icon: DollarSign, description: 'Rate plans and derivations' },
      { label: 'Room Types', path: '/room-types', icon: BedDouble, description: 'Room types and categories' },
      { label: 'Cancellation Policies', path: '/cancellation-policies', icon: ShieldCheck, description: 'Cancellation and guarantee rules' },
      { label: 'Promotion Codes', path: '/promotion-codes', icon: Tag, description: 'Discount codes applied at booking' },
      { label: 'Deposit Policies', path: '/deposit-policies', icon: Percent, description: 'Advance deposit rules and timing' },
      { label: 'Packages', path: '/packages', icon: Package, description: 'Bundled rate packages and inclusions' },
    ],
  },
  {
    title: 'RETAILKEY',
    items: [
      { label: 'POS Terminal', path: '/retailkey', icon: ShoppingBag, description: 'Sell terminal — cart, tender and receipts' },
      { label: 'Products', path: '/retailkey/products', icon: Package, description: 'Retail catalog — SKU, price, departments' },
      { label: 'Stock', path: '/retailkey/stock', icon: PackageSearch, description: 'On-hand levels and inventory adjustments' },
      { label: 'Lookups', path: '/retailkey/lookups', icon: Tag, description: 'Departments, categories, brands, allergens, menu' },
      { label: 'Paid Outs', path: '/retailkey/paid-outs', icon: Banknote, description: 'Till cash disbursements' },
      { label: 'House Accounts', path: '/retailkey/house-accounts', icon: Wallet, description: 'Non-guest charge accounts and balances' },
      { label: 'Vendors', path: '/retailkey/vendors', icon: Truck, description: 'Suppliers and stock receiving' },
      { label: 'RetailKey Reports', path: '/retailkey/reports', icon: BarChart3, description: 'Sales reports, builder and activity log' },
      { label: 'RetailKey Config', path: '/retailkey/config', icon: SlidersHorizontal, description: 'POS, order, night-audit and shift settings' },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { label: 'Property Settings', path: '/property-settings', icon: Settings, description: 'Property data, policies and engagement' },
      { label: 'Charge Types', path: '/charge-types', icon: Coins, description: 'Folio charge types and posting codes' },
      { label: 'Cancellation Reasons', path: '/cancellation-reasons', icon: Ban, description: 'Reason codes for the cancel flow' },
      { label: 'Housekeeping Service Types', path: '/housekeeping-service-types', icon: SprayCan, description: 'Service types for housekeeping tasks' },
      { label: 'Guest Engagement', path: '/guest-engagement', icon: Mail, description: 'Confirmation/arrival letters and online check-out' },
      { label: 'Properties', path: '/properties', icon: Building2, description: 'Manage and switch properties' },
      { label: 'Channels', path: '/channel-manager', icon: Globe, description: 'OTA and channel connections' },
      { label: 'User Management', path: '/users', icon: UsersRound, description: 'Team members and roles' },
      { label: 'Settings', path: '/settings', icon: Settings, description: 'Account and system preferences' },
    ],
  },
];

// ─── Revenue Manager navigation ──────────────────────────────────────────────

const REVENUE_MANAGER_SECTIONS: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Home', path: '/hub', icon: Home },
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    title: 'OPERATIONS',
    items: [
      { label: 'Front Desk', path: '/front-desk', icon: DoorOpen, description: 'Arrivals, departures and availability' },
      { label: 'Reservations', path: '/reservations', icon: Calendar, description: 'Search and manage reservations' },
      { label: 'Group Bookings', path: '/group-bookings', icon: Users, description: 'Group master and blocks' },
      { label: 'Guests', path: '/guests', icon: UserCircle, description: 'Guest profiles and history' },
      { label: 'Bills', path: '/bills', icon: Receipt, description: 'Folios and invoices' },
      { label: 'Shift Management', path: '/shift-management', icon: DollarSign, description: 'Payment summary for a shift' },
      { label: 'House Inventory', path: '/house-inventory', icon: Grid3X3, description: 'Inventory grid by room type and date' },
      { label: 'Reports', path: '/reports', icon: BarChart3, description: 'Dynamic reports and statistics' },
    ],
  },
  {
    title: 'REVENUE MANAGEMENT',
    items: [
      { label: 'Rate Calendar', path: '/rate-calendar', icon: CalendarDays, description: 'Rates and restrictions by date' },
      { label: 'AxisRooms Sync', path: '/rate-parity', icon: RefreshCw, description: 'Rate & availability parity with AxisRooms' },
      { label: 'Rate Administration', path: '/pricing', icon: TrendingUp, description: 'Pricing strategy and overrides' },
      { label: 'Rate Plans', path: '/rate-plans', icon: DollarSign, description: 'Rate plans and derivations' },
      { label: 'Room Types', path: '/room-types', icon: BedDouble, description: 'Room types and categories' },
      { label: 'Cancellation Policies', path: '/cancellation-policies', icon: ShieldCheck, description: 'Cancellation and guarantee rules' },
      { label: 'Promotion Codes', path: '/promotion-codes', icon: Tag, description: 'Discount codes applied at booking' },
      { label: 'Deposit Policies', path: '/deposit-policies', icon: Percent, description: 'Advance deposit rules and timing' },
      { label: 'Packages', path: '/packages', icon: Package, description: 'Bundled rate packages and inclusions' },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { label: 'Properties', path: '/properties', icon: Building2, readOnly: true, description: 'View properties' },
      { label: 'Channels', path: '/channel-manager', icon: Globe, description: 'OTA and channel connections' },
    ],
  },
];

// ─── Operations staff navigation ─────────────────────────────────────────────

const OPERATIONS_SECTIONS: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Home', path: '/hub', icon: Home },
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    title: 'OPERATIONS',
    items: [
      { label: 'Front Desk', path: '/front-desk', icon: DoorOpen, description: 'Arrivals, departures and availability' },
      { label: 'Reservations', path: '/reservations', icon: Calendar, readOnly: true, description: 'View reservations' },
      { label: 'Guests', path: '/guests', icon: UserCircle, readOnly: true, description: 'View guest profiles' },
      { label: 'Housekeeping', path: '/housekeeping', icon: SprayCan, description: 'Room status and service tasks' },
      { label: 'Maintenance', path: '/maintenance', icon: Wrench, description: 'Issues and out-of-order rooms' },
      { label: 'Misc Expense', path: '/misc-expense', icon: Receipt, description: 'Log miscellaneous expenses' },
      { label: 'Misc Revenue', path: '/misc-revenue', icon: TrendingUp, description: 'Log miscellaneous revenue' },
      { label: 'House Inventory', path: '/house-inventory', icon: Grid3X3, description: 'Inventory grid by room type and date' },
      { label: 'Lost & Found', path: '/lost-found', icon: PackageSearch, description: 'Track lost-and-found items' },
    ],
  },
  {
    title: 'RETAILKEY',
    items: [
      { label: 'POS Terminal', path: '/retailkey', icon: ShoppingBag, description: 'Sell terminal — cart, tender and receipts' },
      { label: 'Products', path: '/retailkey/products', icon: Package, description: 'View the retail catalog' },
      { label: 'Stock', path: '/retailkey/stock', icon: PackageSearch, description: 'On-hand levels and inventory adjustments' },
      { label: 'Paid Outs', path: '/retailkey/paid-outs', icon: Banknote, description: 'Till cash disbursements' },
      { label: 'House Accounts', path: '/retailkey/house-accounts', icon: Wallet, description: 'Non-guest charge accounts and balances' },
    ],
  },
];

// ─── Owner navigation ────────────────────────────────────────────────────────

const OWNER_SECTIONS: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Home', path: '/hub', icon: Home },
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { label: 'Owner Dashboard', path: '/owner-dashboard', icon: Briefcase, description: 'Owner-facing performance view' },
    ],
  },
];

// ─── Exports ─────────────────────────────────────────────────────────────────

export const NAV_SECTIONS_BY_ROLE: Record<UserRole, NavSection[]> = {
  [UserRole.SUPER_ADMIN]: ADMIN_SECTIONS,
  [UserRole.ADMIN]: ADMIN_SECTIONS,
  [UserRole.REVENUE_MANAGER]: REVENUE_MANAGER_SECTIONS,
  [UserRole.OPERATIONS]: OPERATIONS_SECTIONS,
  [UserRole.OWNER]: OWNER_SECTIONS,
};

export function getNavSections(role: UserRole): NavSection[] {
  return NAV_SECTIONS_BY_ROLE[role] ?? [];
}

// Flat list for backward compatibility
export const NAV_ITEMS_BY_ROLE: Record<UserRole, NavItem[]> = {
  [UserRole.SUPER_ADMIN]: ADMIN_SECTIONS.flatMap((s) => s.items),
  [UserRole.ADMIN]: ADMIN_SECTIONS.flatMap((s) => s.items),
  [UserRole.REVENUE_MANAGER]: REVENUE_MANAGER_SECTIONS.flatMap((s) => s.items),
  [UserRole.OPERATIONS]: OPERATIONS_SECTIONS.flatMap((s) => s.items),
  [UserRole.OWNER]: OWNER_SECTIONS.flatMap((s) => s.items),
};

export function getNavItems(role: UserRole): NavItem[] {
  return NAV_ITEMS_BY_ROLE[role] ?? [];
}
